import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import get from "lodash/get";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { addResizedColumns } from "./useTableConfig";
import { ROLES_TABLE_CONSTANTS } from "routes/admin/components/roles_table/RolesTableConstants";
import PermissionService from "services/Permission";
import RoleService from "services/Role";
import UserService from "services/User";
import { getRolesTableColumns } from "routes/admin/components/roles_table/RolesTableColumnsAndFilters";

export const invalidateRolesQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [ROLES_TABLE_CONSTANTS.queryKey],
  });
};

// `create` and `update` have no error toast: BaseForm already shows a failed
// submit in the form's error banner.
export const useRoleMutations = () => {
  const create = useMutation({
    mutationFn: ({ name, permissions }) =>
      RoleService.createRole(name, permissions),
    onSuccess: () => invalidateRolesQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, name, permissions }) =>
      RoleService.updateRole(id, name, permissions),
    onSuccess: () => invalidateRolesQuery(),
  });

  const remove = useMutation({
    mutationFn: id => RoleService.deleteRole(id),
    onSuccess: () => {
      invalidateRolesQuery();
      toast.success("Role deleted");
    },
    onError: err => toast.error(formatError(err, "Failed to delete role")),
  });

  return { create, update, remove };
};

// The swagger document and the category patterns only change on a deploy, so
// the catalog is fetched once per session.
export const usePermissionCatalog = () =>
  useQuery({
    queryKey: ["permission-catalog"],
    queryFn: async () => {
      const [paths, categories] = await Promise.all([
        PermissionService.getPermissions(),
        PermissionService.getPermissionCategories(),
      ]);
      return { paths: paths ?? {}, categories };
    },
    staleTime: Infinity,
  });

export const useRolesTable = ({ onEdit, onClone, onDelete }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getRolesTableColumns({ onEdit, onClone, onDelete });
    const rolesTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${ROLES_TABLE_CONSTANTS.tableName}`,
      {}
    );
    return addResizedColumns({
      columns: initialColumns,
      resizedColumns: rolesTableSettings?.resized_columns || {},
    });
  }, [tableSettings, isTableSettingsFetched, onEdit, onClone, onDelete]);

  return { columns };
};
